import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../../globals";
import { useBorrador } from "./borradores";

import HttpManager from "../../../services/HttpManager";
import { telefonoConLada } from "../../../utils/telefonoBonito";
import { BandejaDePersona, ProspectCatalogs, ProspectDetails, ProspectEvent, WaChannel, WaState } from "../../../services/v2/prospects.service";
import { cuandoViene, dinero, DEPTS_WAPP, errorTexto, etapaClase, iniciales, KIND_FILTROS, KIND_LABEL, limpiarWa, localMxAIso, nombreCompleto, nombreReal, SIMBOLO_PLANTEL, waDiceAlgo, tonoAvatar } from "./salesHelpers";
import { BuscadorChat, ItemBusqueda, pedacitoDe } from "./buscarMensajes";
import Conversacion from "./conversacion";
import Compositor from "./compositor";
import LadoCliente from "./ladoCliente";
import { AvisoSoloVer, useAcceso } from "./soloVer";
import { EquipoLinea } from "./equipoChips";
import PerfilPersona, { AnclaPerfil } from "./perfilPersona";
import EditarDatos from "./editarDatos";
import Llamada from "./llamada";
import PromoModal from "./promoModal";
import { aMarcable, FinLlamada, resultadoSinRespuesta, telefono } from "./telefono/telefono";
import { mostrarLlamada, useTelefono } from "./telefono/telefonoUI";
import { intervaloRespaldo, push } from "./push";
import ReasignarDestino from "./reasignarDestino";
import { ModalCita } from "./formCita";
import { marcador as autoMarcador } from "./marcador";

interface Props {
	campusId: number;
	prospectId: number;
	catalogs: ProspectCatalogs | null;
	onChanged: () => void;
	onOpen: (id: number) => void;
	onBack: () => void;
	/** Marcador: la ficha llega con la llamada ya abierta y al guardar pasa al siguiente. */
	marcador?: { onResultado: (resultado: string) => void; autoLlamar: boolean; campaignContactId?: number | null; campaignId?: number | null; callerId?: string | null } | null;
	/** Llevar la bandeja a una vista ("Ver lista" de la promo: Matriculado · Califican a promo). */
	onVista?: (etapa: number, view: string) => void;
	/** Texto del botón de regreso: "Mi cartera", "Todo el plantel"… */
	volverTexto?: string;
	/** WappChat: "Ver en CRM" abre a esta persona en la vista tipo Zoho. */
	onVerCrm?: (id: number) => void;
	/** Abierta desde la bandeja de un área (Caja, Kits, Académico): sin status de ventas; tomar, resolver y pasar. */
	bandeja?: { dept: string; channelId: number | null; supervisa: boolean } | null;
	/** Abrir la conversación en un mensaje (resultado de buscar en la lista): n cambia en cada clic. */
	irAEvento?: { prospectId: number; eventId: number; n: number } | null;
}

const PAGE = 60;

type Viene = { key: string; que: string; detalle?: string | null; v: NonNullable<ReturnType<typeof cuandoViene>> };

/** "Lo que viene": seguimiento agendado, gestiones con fecha, próxima mensualidad, fin de curso. Lo más próximo primero. */
const LoQueViene: React.FC<{ det: ProspectDetails; campusId: number; onAgendar: () => void; corto?: boolean }> = ({ det, campusId, onAgendar, corto }) => {
	const p = det.prospect;
	const act = det.wapp?.active || null;
	const sim = SIMBOLO_PLANTEL[p.campus_id || campusId] || "";
	const items: Viene[] = [];
	const push = (key: string, que: string, fecha: string | null | undefined, detalle?: string | null) => {
		const v = cuandoViene(fecha);
		if (v) items.push({ key, que, detalle, v });
	};
	push("seg", p.next_action_reason || "Seguimiento", p.next_action_at);
	det.gestiones.forEach((g) => push("g" + g.id, "Gestión: " + g.objetivo, g.next_action_at, g.next_action_reason || g.owner_name));
	if (act?.proxima_fecha) {
		push("mens", "Mensualidad" + (act.proxima_monto ? " " + dinero(act.proxima_monto, sim) : ""), act.proxima_fecha, act.vencidas ? act.vencidas + " vencida(s)" : null);
		if (act.vencidas) items[items.length - 1].v.tono = "vencido";
	}
	if (act?.end_date && act.termina_en_dias !== null && act.termina_en_dias >= 0 && act.termina_en_dias <= 45) push("fin", "Termina su curso", act.end_date, act.modulo);
	items.sort((a, b) => a.v.orden - b.v.orden);
	// WappChat (encabezado): solo actividades, con el color de Zoho (Esteban, 2026-10-05: la mensualidad sobra y puede
	// estar mal); lo más próximo y cuántas más.
	if (corto) {
		const acts = items.filter((x) => x.key === "seg" || x.key.startsWith("g"));
		const i = acts[0];
		return (
			<span className="cv-viene-corto">
				{i ? (
					<button className={"cv-viene-item " + i.v.tono} onClick={onAgendar} title={acts.map((x) => x.v.texto + ": " + x.que).join("\n")}>
						<span className="cuando">{i.v.texto}</span> <span className="que">{i.que}</span>{acts.length > 1 ? <span className="det"> +{acts.length - 1}</span> : null}
					</button>
				) : null}
				{!p.next_action_at ? <button className="cv-viene-mas" onClick={onAgendar}>+ Agendar seguimiento</button> : null}
			</span>
		);
	}
	return (
		<div className="cv-viene">
			<span className="cv-rotulo">Lo que viene</span>
			{items.map((i) => (
				<div key={i.key} className={"cv-viene-item " + i.v.tono} onClick={i.key === "seg" ? onAgendar : undefined} role={i.key === "seg" ? "button" : undefined}>
					<span className="cuando">{i.v.texto}</span>
					<span className="que">{i.que}{i.detalle ? <span className="det"> · {i.detalle}</span> : null}</span>
				</div>
			))}
			{!p.next_action_at ? <button className="cv-viene-mas" onClick={onAgendar}>+ Agendar seguimiento</button> : null}
		</div>
	);
};

/**
 * La ficha del cliente como en el lienzo (19-sep): arriba quién es y qué hacer (Llamar, Agendar, Inscribir),
 * al centro la conversación de WhatsApp con notas y llamadas mezcladas, abajo para escribir, a la derecha el contexto.
 */
const ClienteView: React.FC<Props> = ({ campusId, prospectId, catalogs, onChanged, onOpen, onBack, marcador, onVista, volverTexto, onVerCrm, bandeja, irAEvento }) => {
	const http = HttpManager.getInstance();
	const yoId: number | null = useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.id ?? null);
	// En qué bandejas de área está la persona (Caja, Kits...): quién la atiende y si alguien se la pasó.
	const [enBandejas, setEnBandejas] = useState<BandejaDePersona[]>([]);
	const [pasarA, setPasarA] = useState<string>("");
	// "Otro plantel…" al pasar a un departamento (solo supervisión): null = el plantel de la persona.
	const [pasarPlantel, setPasarPlantel] = useState<number | null>(null);
	const [pasarPlanteles, setPasarPlanteles] = useState<{ id: number; name: string }[] | null>(null);
	const [pasarNota, setPasarNota] = useState<string>("");
	const navigate = useNavigate();
	// Tarjeta de perfil de una asesora (dueñas en "Equipo:" y quien escribió cada mensaje), como en WappTeam (Esteban, 2026-10-25).
	const [tarjeta, setTarjeta] = useState<AnclaPerfil | null>(null);
	const verPerfil = (id: number | string, e: React.MouseEvent) => { e.stopPropagation(); setTarjeta({ userId: id, x: e.clientX, y: e.clientY }); };
	const [det, setDet] = useState<ProspectDetails | null>(null);
	const av = useAcceso(det);
	const [events, setEvents] = useState<ProspectEvent[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [cargando, setCargando] = useState<boolean>(false);
	const [filtro, setFiltro] = useState<string>("conversacion");
	// La lupa del encabezado: busca en todo el historial (servidor) y lleva al mensaje (salto: n cambia en cada salto).
	const [buscarAbierto, setBuscarAbierto] = useState<boolean>(false);
	const [salto, setSalto] = useState<{ id: number; n: number } | null>(null);
	const saltoUsado = useRef<number>(0);
	const [wa, setWa] = useState<{ state: WaState; channels: WaChannel[] } | null>(null);
	const [canal, setCanal] = useState<number | null>(null);
	const [texto, setTexto] = useBorrador(prospectId ? "chat:" + prospectId : null);   // borrador por cliente (borradores.ts)
	const [panel, setPanel] = useState<"agendar" | "reasignar" | "vincular" | "pasar" | "compartir" | "contrato" | null>(null);
	// "Enviar Contrato" (2026-10-12): el PDF del contrato por WhatsApp; con la ventana cerrada va por plantilla.
	const [contratos, setContratos] = useState<{ id: number; nombre: string | null; fecha: string; curso: string | null; horario: string | null; firmado: boolean }[] | null>(null);
	const [contratoSel, setContratoSel] = useState<number | null>(null);
	// Desde el perfil de la alumna (Documentos → "Enviar por WhatsApp"): /wappchat?id=N&contrato=M abre este panel con ese contrato.
	useEffect(() => {
		const p = new URLSearchParams(window.location.search);
		const pedido = Number(p.get("contrato") || 0);
		if (!pedido || !catalogs?.contratoWa) return;
		p.delete("contrato");
		window.history.replaceState(null, "", window.location.pathname + (p.toString() ? "?" + p.toString() : ""));
		setPanel("contrato"); setContratos(null); setContratoSel(pedido);
		http.prospectsService.contratosWa(campusId, prospectId).then((r) => {
			setContratos(r.data.contratos);
			setContratoSel(r.data.contratos.some((c) => c.id === pedido) ? pedido : r.data.contratos[0]?.id ?? null);
		}).catch((e) => { setContratos([]); setError(errorTexto(e)); });
	}, [prospectId, catalogs?.contratoWa]);
	// Compartir en Equipo: a qué canal y con qué nota.
	const [canalesEq, setCanalesEq] = useState<{ id: number; nombre: string; tipo: string }[]>([]);
	const [compartirEn, setCompartirEn] = useState<string>("");
	const [compartirNota, setCompartirNota] = useState<string>("");
	const [modoComp, setModoComp] = useState<"mensaje" | "nota">("mensaje");
	// Responder a un mensaje de WhatsApp en específico (la cita arriba).
	const [respondiendo, setRespondiendo] = useState<{ id: number; body: string | null; nuestro: boolean } | null>(null);
	// Teléfono: la conversación y el panel del cliente (Qué le interesa, Horarios, Cotización) van en pestañas.
	const [vistaMovil, setVistaMovil] = useState<"chat" | "cliente">("chat");
	// Computadora: la ficha de la derecha se abre con el botón "Ficha" y se recuerda (Esteban: más espacio para la conversación).
	const [verFicha, setVerFicha] = useState<boolean>(() => { try { return localStorage.getItem("wchat.ficha") === "1"; } catch { return false; } });
	const alternarFicha = () => setVerFicha((v) => { try { localStorage.setItem("wchat.ficha", v ? "0" : "1"); } catch { /* sin almacenamiento */ } return !v; });
	// En tablet vertical la Ficha es una hoja encima del chat: Esc la cierra (Esteban, 2026-10-22).
	useEffect(() => {
		if (!verFicha) return;
		const k = (e: KeyboardEvent) => { if (e.key === "Escape" && window.matchMedia("(min-width: 701px) and (max-width: 900px)").matches) alternarFicha(); };
		document.addEventListener("keydown", k);
		return () => document.removeEventListener("keydown", k);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [verFicha]);
	const [foco, setFoco] = useState<number>(0);
	// Status de un clic: solo los que piden motivo (Potencial) abren el selector.
	const [pideMotivo, setPideMotivo] = useState<number | null>(null);
	const [modal, setModal] = useState<"editar" | "llamar" | "promo" | null>(null);
	// Promesa con fecha (Esteban, 2026-10-13): el status Promesa y el atajo "Promesa con Cita" abren la cita ahí mismo.
	const [cita, setCita] = useState<{ promesa: boolean } | null>(null);
	const [mas, setMas] = useState<boolean>(false);
	// Esc cierra el menú ⋮ (Esteban, 2026-10-22).
	useEffect(() => {
		if (!mas) return;
		const k = (e: KeyboardEvent) => { if (e.key === "Escape") setMas(false); };
		document.addEventListener("keydown", k);
		return () => document.removeEventListener("keydown", k);
	}, [mas]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// formularios de acciones
	const [statusId, setStatusId] = useState<number>(0);
	const [motivo, setMotivo] = useState<string>("");
	const [segFecha, setSegFecha] = useState<string>("");   // día (YYYY-MM-DD)
	// Hora opcional (Esteban, 2026-10-14): sin hora va todo el día; el servidor lo guarda a las 11:59 pm y no vence antes.
	const [segHora, setSegHora] = useState<string | null>(null);
	const segAt = () => localMxAIso(segFecha + "T" + (segHora || "23:59"));
	const [segMotivo, setSegMotivo] = useState<string>("");
	const [reasignarA, setReasignarA] = useState<string>("");
	const [reasignarPlantel, setReasignarPlantel] = useState<number | null>(null);
	const [vincTel, setVincTel] = useState<string>("");
	const [vincTipo, setVincTipo] = useState<string>("padre_madre");
	const [vincMatch, setVincMatch] = useState<any>(null);
	const vivo = useRef<number>(prospectId);
	const autoPendiente = useRef<boolean>(false);
	const tel = useTelefono();
	const [fin, setFin] = useState<FinLlamada | null>(null);
	// Al colgar una llamada de esta persona (saliente o entrante ligada) se abre "cómo terminó" con la duración ya puesta.
	// (El alto con el teclado abierto lo maneja altoVisible: el iPhone acomoda la caja solo.)
	// Si no la contestaron, se registra sola ("No contestó" / "Ocupado") y el auto-marcador sigue con la siguiente
	// (Esteban, 2026-10-09); si la contestaron, se pregunta cómo terminó.
	const [avisoLlamada, setAvisoLlamada] = useState<string | null>(null);
	const marcadorRef = useRef(marcador);
	marcadorRef.current = marcador;
	const despuesDeRegistrar = useRef<((r: string) => void) | null>(null);
	useEffect(() => telefono.alTerminar((f) => {
		if (f.prospectId !== prospectId) return;
		// La llamó el auto-marcador del panel: su tarjeta de cierre la registra (no dos veces).
		if (!marcadorRef.current && autoMarcador.atiende(f.prospectId)) { window.setTimeout(() => despuesDeRegistrar.current?.("marcador"), 1500); return; }
		const sola = resultadoSinRespuesta(f);
		if (!sola) { setFin(f); setModal("llamar"); return; }
		HttpManager.getInstance().prospectsService.call(campusId, prospectId, { result: sola, source: marcadorRef.current ? "marcador" : "manual",
			durationSec: f.durationSec, callId: f.callId, campaignContactId: f.campaignContactId || marcadorRef.current?.campaignContactId || undefined })
			.then(() => { setAvisoLlamada(sola === "ocupado" ? "Llamada registrada: ocupado." : "Llamada registrada: no contestó."); window.setTimeout(() => setAvisoLlamada(null), 4000); despuesDeRegistrar.current?.(sola); })
			.catch(() => { setFin(f); setModal("llamar"); });
	}), [prospectId, campusId]);

	const kinds = useMemo(() => KIND_FILTROS.find((f) => f.key === filtro)?.kinds || [], [filtro]);

	const cargarBandejas = useCallback(async () => {
		try {
			const r = await http.prospectsService.bandejasDePersona(campusId, prospectId);
			if (vivo.current === prospectId) setEnBandejas(r.data.bandejas || []);
		} catch (e) { setEnBandejas([]); }
	}, [campusId, prospectId]);

	const cargarDetalle = useCallback(async () => {
		try {
			const r = await http.prospectsService.details(campusId, prospectId);
			if (vivo.current !== prospectId) return;
			setDet(r.data);
			setStatusId(r.data.prospect.status_id);
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, prospectId]);

	const cargarWa = useCallback(async (ch?: number | null) => {
		try {
			const r = await http.prospectsService.whatsapp(campusId, prospectId, ch ?? canal);
			if (vivo.current === prospectId) setWa(r.data);
		} catch (e) { setWa(null); }
	}, [campusId, prospectId, canal]);

	// La API da lo más nuevo primero; en pantalla va al revés (lo nuevo abajo).
	// alrededor (solo "reset"): trae de lo más nuevo hasta ese evento y después lo enseña resaltado (buscar).
	const cargarEventos = useCallback(async (p: number, modo: "reset" | "anteriores" | "nuevos", alrededor?: number) => {
		if (modo !== "nuevos") setCargando(true);
		try {
			// Abrirla la deja leída; lo que llega con ella abierta, solo si la pantalla está a la vista (no leídos como WhatsApp).
			const r = await http.prospectsService.events(campusId, prospectId, { kind: kinds, alrededor: modo === "reset" ? alrededor : undefined,
				leido: p === 1 && (modo !== "nuevos" || document.visibilityState === "visible") }, PAGE, p);
			if (vivo.current !== prospectId) return;
			const lote = [...r.data.data].reverse();
			setTotal(r.data.total);
			if (modo === "reset") { setEvents(lote); setPage(alrededor ? Math.max(1, Number(r.data.page) || 1) : 1); if (alrededor) setSalto({ id: alrededor, n: Date.now() }); }
			else if (modo === "anteriores") { setEvents((prev) => [...lote.filter((e) => !prev.some((x) => x.id === e.id)), ...prev]); setPage(p); }
			else {
				setEvents((prev) => {
					const nuevos = lote.filter((e) => !prev.some((x) => x.id === e.id));
					// estados de entrega (✓✓, leído) también cambian en mensajes ya pintados
					let cambio = false;
					const act = prev.map((x) => { const y = lote.find((e) => e.id === x.id); if (y && y.wa_status !== x.wa_status) { cambio = true; return { ...x, wa_status: y.wa_status }; } return x; });
					// Sin nada nuevo ni palomitas distintas: la misma lista (no vuelve a pintar la conversación).
					return nuevos.length ? [...act, ...nuevos] : cambio ? act : prev;
				});
			}
		} catch (e) { if (modo !== "nuevos") setError(errorTexto(e)); }
		finally { if (modo !== "nuevos") setCargando(false); }
	}, [campusId, prospectId, kinds]);

	// El texto que se acaba de mandar sale al instante con relojito, como WhatsApp (Esteban, 2026-10-18). Cuando llega el
	// mensaje de verdad (wapp-push o la recarga al terminar el envío) ocupa su lugar; si no salió, se quita.
	const [enCamino, setEnCamino] = useState<{ clave: number; prospectId: number; body: string; at: string; desde: number; id: number | null;
		cita: { id: number; body: string | null; nuestro: boolean } | null }[]>([]);
	const yoNombre: string = useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.first_name || "");
	const onSale = (m: { clave: number; body: string; cita: { id: number; body: string | null; nuestro: boolean } | null }) =>
		setEnCamino((prev) => [...prev, { ...m, prospectId, at: new Date().toISOString(), desde: events.reduce((x, e) => Math.max(x, e.id), 0), id: null }]);
	const onSalio = (clave: number, id: number | null) => {
		const quitar = () => setEnCamino((prev) => prev.filter((m) => m.clave !== clave));
		if (id === null) { quitar(); return; }
		setEnCamino((prev) => prev.map((m) => (m.clave === clave ? { ...m, id } : m)));
		setTimeout(quitar, 20000);   // por si la recarga no lo trae: el respaldo de cada 2 min lo pinta
	};
	// Cada mensaje de verdad toma el lugar de uno solo en camino: por id si ya se sabe, si no por el texto (llegó por push antes que la respuesta).
	const eventosVista = useMemo(() => {
		if (!enCamino.length || (kinds.length && !kinds.includes("whatsapp"))) return events;
		const usados = new Set<number>();
		const faltan = enCamino.filter((m) => {
			if (m.prospectId !== prospectId) return false;
			const real = events.find((e) => !usados.has(e.id) && (m.id !== null ? e.id === m.id
				: e.id > m.desde && e.kind === "whatsapp" && e.direction === "out" && (e.body || "").trim() === m.body));
			if (real) usados.add(real.id);
			return !real;
		});
		return faltan.length ? [...events, ...faltan.map((m) => ({ id: -m.clave, kind: "whatsapp", direction: "out", body: m.body, author_user_id: yoId,
			author_name: yoNombre || null, occurred_at: m.at, origen: "wapp", meta: { enCamino: true }, wa_status: null,
			...(m.cita ? { cita: m.cita } : {}) } as ProspectEvent))] : events;
	}, [events, enCamino, kinds, prospectId, yoId, yoNombre]);

	useEffect(() => {
		vivo.current = prospectId;
		setDet(null); setEvents([]); setPanel(null); setModal(null); setCita(null); setError(null); setRespondiendo(null); setMas(false); setModoComp("mensaje"); setPideMotivo(null);
		setBuscarAbierto(false); setSalto(null);
		// Desde una bandeja se contesta por el número del área por el que escribió.
		setCanal(bandeja?.channelId ?? null);
		setEnBandejas([]);
		cargarDetalle();
		cargarWa(bandeja?.channelId ?? null);
		cargarBandejas();
		setFin(null);
		autoPendiente.current = !!marcador?.autoLlamar;
	}, [prospectId]);
	// Marcador automático: en cuanto carga la ficha, con el teléfono encendido marca solo; si no, abre el registro manual.
	useEffect(() => {
		if (!det || !autoPendiente.current || det.prospect.id !== prospectId) return;
		autoPendiente.current = false;
		llamarAhora().then((ok) => { if (!ok) setModal("llamar"); });
	}, [det]);
	// Un resultado de la lista trae a qué mensaje ir. Si el filtro de "Ver" no enseña WhatsApp, primero se pone Conversación
	// (cambia kinds y este efecto vuelve a correr). Al abrir a la persona, la primera carga ya llega hasta ese mensaje.
	const irPendiente = (): number | undefined => {
		const ir = irAEvento && irAEvento.prospectId === prospectId && saltoUsado.current !== irAEvento.n ? irAEvento : null;
		if (!ir) return undefined;
		if (kinds.length && !kinds.includes("whatsapp")) { setFiltro("conversacion"); return -1; }
		saltoUsado.current = ir.n;
		return ir.eventId;
	};
	useEffect(() => {
		const ir = irPendiente();
		if (ir === -1) return;
		cargarEventos(1, "reset", ir);
	}, [prospectId, kinds]);
	useEffect(() => {
		const ir = irPendiente();
		if (ir && ir !== -1) saltar(ir);
	}, [irAEvento?.n]);
	/** Ir a un mensaje: si ya está cargado, ahí mismo; si no, se trae la conversación hasta él. */
	const saltar = (id: number) => {
		if (events.some((e) => e.id === id)) setSalto({ id, n: Date.now() });
		else cargarEventos(1, "reset", id);
	};
	const buscarEnChat = useCallback(async (q: string): Promise<ItemBusqueda[]> => {
		const r = await http.prospectsService.events(campusId, prospectId, { kind: kinds, q }, 50, 1);
		return r.data.data.map((e) => ({
			id: e.id, fecha: e.occurred_at, texto: pedacitoDe(e.body, q) || "Archivo",
			quien: e.kind === "whatsapp" ? (e.direction === "out" ? (e.author_name ? String(e.author_name).split(" ")[0] : "Nosotros") : (det?.prospect?.first_name || "").split(" ")[0] || "Cliente")
				: KIND_LABEL[e.kind] || e.kind,
		}));
	}, [campusId, prospectId, kinds, det]);

	// Lo que llegue mientras está abierta (WhatsApp entrante, palomitas): al instante por wapp-push; si no hay conexión,
	// la consulta de respaldo (15 s sin conexión, 2 min con ella).
	useEffect(() => {
		const traer = () => { if (document.visibilityState === "visible") { cargarEventos(1, "nuevos"); cargarWa(); } };
		const off = push.on("wa", (d) => { if (Number(d?.p) === prospectId) traer(); });
		let t: any;
		const ciclo = () => { t = setTimeout(() => { traer(); ciclo(); }, intervaloRespaldo()); };
		ciclo();
		return () => { off(); clearTimeout(t); };
	}, [cargarEventos, cargarWa]);

	const accion = async (fn: () => Promise<any>) => {
		setBusy(true); setError(null);
		try {
			await fn();
			await Promise.all([cargarDetalle(), cargarEventos(1, "reset")]);
			setPanel(null);
			onChanged();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	/**
	 * Marca por el teléfono de WAPP; si se está reconectando, lo reconecta y marca en cuanto queda listo (Esteban,
	 * 2026-10-12: el teléfono siempre encendido). false solo si no se pudo: entonces se registra a mano.
	 */
	async function llamarAhora(): Promise<boolean> {
		const ph = det?.prospect?.phone_e164;
		if (!ph || telefono.ocupado) return false;
		if (!telefono.listo) setAvisoLlamada("Conectando el teléfono…");
		const id = await telefono.llamarYa(aMarcable(ph), { prospectId, name: det ? nombreCompleto(det.prospect) : null, campaignContactId: marcador?.campaignContactId || null, campaignId: marcador?.campaignId || null, callerId: marcador?.callerId || null });
		setAvisoLlamada(null);
		return !!id;
	}
	const ponerStatus = (id: number, conMotivo: string | null = null) => {
		const s = catalogs?.statuses.find((x) => x.id === id);
		if (!s || !s.bool_manual) return;
		if (s.bool_needs_reason && !conMotivo) { setPideMotivo(id); setMotivo(""); return; }
		if (id === det?.prospect.status_id && (!s.bool_needs_reason || conMotivo === det?.prospect.potencial_motivo)) return;
		setPideMotivo(null);
		if (id === 4) { setCita({ promesa: true }); return; }
		accion(() => http.prospectsService.status(campusId, prospectId, id, s.bool_needs_reason ? conMotivo : null));
	};
	/** "Al Marcador ↑": hasta arriba de mi lista de llamadas de hoy. */
	const alMarcador = () => http.prospectsService.dialerPin(campusId, prospectId)
		.then(() => { autoMarcador.refrescar(); setAvisoLlamada("Listo: va hasta arriba de tu marcador de hoy."); window.setTimeout(() => setAvisoLlamada(null), 3500); })
		.catch((e) => setError(errorTexto(e)));
	const guardarStatus = () => pideMotivo && motivo && accion(() => http.prospectsService.status(campusId, prospectId, pideMotivo, motivo)).then(() => setPideMotivo(null));
	/** ← en el teléfono: de la ficha regresa a la conversación (como WhatsApp); de la conversación, a la lista. */
	const volver = () => { if (vistaMovil === "cliente") setVistaMovil("chat"); else onBack(); };
	/** Tocar el nombre: en el teléfono abre la ficha del cliente; en la computadora, el panel de la derecha. */
	const tocarNombre = () => { if (window.matchMedia("(max-width: 700px)").matches) setVistaMovil(vistaMovil === "cliente" ? "chat" : "cliente"); else alternarFicha(); };
	const esSpam = det?.prospect?.status_id === 8;
	const marcarSpam = () => {
		if (!window.confirm("¿Marcar este número como spam? Sale de las listas y deja de avisar. La conversación no se borra y se puede quitar cambiando el status.")) return;
		accion(() => http.prospectsService.status(campusId, prospectId, 8, null));
	};
	/** "+ Nota" de arriba: abre el cuadro de abajo con el cursor puesto. */
	const escribir = (m: "mensaje" | "nota") => {
		setVistaMovil("chat");
		setModoComp(m);
		setFoco((n) => n + 1);
	};
	const guardarSeguimiento = (quitar = false) => accion(() => http.prospectsService.nextAction(campusId, prospectId, quitar ? null : segAt(), quitar ? null : segMotivo));
	const guardarReasignar = async () => {
		if (!reasignarA) return;
		if (!reasignarPlantel) { accion(() => http.prospectsService.assign(campusId, prospectId, reasignarA, motivo || null)); return; }
		// A otro plantel: puede quedar fuera de mi territorio, así que no se recarga la ficha si ya no se ve.
		setBusy(true); setError(null);
		try {
			await http.prospectsService.assign(campusId, prospectId, reasignarA, motivo || null, reasignarPlantel);
			setPanel(null); setReasignarA(""); setReasignarPlantel(null); onChanged();
			try { const r = await http.prospectsService.details(campusId, prospectId); setDet(r.data); cargarEventos(1, "reset"); }
			catch (e) { /* ya quedó fuera de mi territorio: la lista se refresca sola */ }
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const buscarVinculo = async () => {
		setVincMatch(null);
		try {
			const r = await http.prospectsService.lookupPhone(campusId, vincTel);
			setVincMatch(r.data.match || { none: true, valid: r.data.valid });
		} catch (e) { setError(errorTexto(e)); }
	};
	const guardarVinculo = () => vincMatch?.id && accion(() => http.prospectsService.addLink(campusId, prospectId, vincMatch.id, vincTipo));
	const quitarVinculo = (linkId: number) => accion(() => http.prospectsService.removeLink(campusId, prospectId, linkId));
	const guardarPasar = () => pasarA && accion(async () => {
		await http.prospectsService.pasar(campusId, prospectId, pasarA, pasarNota.trim() || null, pasarPlantel);
		setPasarA(""); setPasarNota(""); setPasarPlantel(null); setPasarPlanteles(null);
		await cargarBandejas();
	});
	const enEsta = bandeja ? enBandejas.find((b) => b.dept === bandeja.dept) || null : null;
	const cambiarBandeja = (a: "tomar" | "soltar" | "resolver" | "reabrir") => bandeja && accion(async () => {
		await http.prospectsService.bandejaEstado(campusId, prospectId, bandeja.dept, a);
		await cargarBandejas();
	});

	/** Siguiente paso (lienzo): atajos que ponen status y seguimiento de una vez. */
	const paso = (p: "promesa" | "llamar" | "no_interesa") => {
		if (p === "no_interesa") {
			return accion(() => http.prospectsService.status(campusId, prospectId, 5, "no le interesa por ahora"));
		}
		if (p === "promesa") { setCita({ promesa: det?.prospect.status_id !== 4 }); return; }
		setSegMotivo("Volver a llamar");
		setPanel("agendar");
	};
	const guardarAgenda = async () => {
		try { segAt(); } catch (e) { setError(errorTexto(e)); return; }
		if (segMotivo === "Promesa de visita" && det && det.prospect.status_id !== 4) {
			await accion(async () => {
				await http.prospectsService.status(campusId, prospectId, 4, null);
				await http.prospectsService.nextAction(campusId, prospectId, segAt(), segMotivo);
			});
		} else {
			await guardarSeguimiento(false);
		}
	};

	if (!det) {
		return <div className="ventas-cliente"><div className="ventas-vacio">{error || "Cargando…"}</div></div>;
	}
	const p = det.prospect;
	const ubicacion = (() => {
		const c = catalogs?.campuses.find((x) => x.id === (p.campus_id || campusId));
		return c?.address ? `${c.name}\n${c.address}` : null;
	})();

	despuesDeRegistrar.current = (r: string) => { cargarEventos(1, "nuevos"); cargarDetalle(); onChanged(); marcador?.onResultado(r); };
	return (
		<div className={"ventas-cliente cv" + (av.soloVer ? " solo-ver" : "")}>
			{/* Encabezado como WhatsApp (Esteban, 2026-10-05): ←, quién (toca para la ficha), llamar y ⋮. Lo demás vive en el menú;
			    en la computadora quedan a la vista Agendar, Pasar a e Inscribir. */}
			<div className="cli-cab wchat-cab">
				<div className="cab-fila">
					<button className="cv-volver" onClick={volver} aria-label={vistaMovil === "cliente" ? "Volver a la conversación" : "Volver a " + (volverTexto || "Mi cartera")}>
						<span aria-hidden="true">←</span> <span className="txt">{(volverTexto || "Mi cartera").toUpperCase()}</span></button>
					<div className="quien" role="button" tabIndex={0} onClick={tocarNombre} onKeyDown={(e) => { if (e.key === "Enter") tocarNombre(); }} aria-label={"Ficha de " + nombreCompleto(p)}>
						<span className={"av " + tonoAvatar(p.id)} aria-hidden="true">{iniciales(p)}</span>
						<span className="quien-txt">
							{/* PC: nombre, teléfono y etapa en un renglón; abajo el equipo en una línea (Esteban, 2026-10-15). */}
							<span className="quien-l1">
							<span className="nombre">{nombreCompleto(p)}
								{/* Su nombre en WhatsApp, chiquito junto al del CRM (Esteban, 2026-10-05), solo si es distinto. */}
								{det.waNombre && waDiceAlgo(det.waNombre) && limpiarWa(det.waNombre).toLowerCase() !== nombreCompleto(p).trim().toLowerCase()
									? <span className="wa-nombre" title="Nombre en su WhatsApp"> ~{det.waNombre}</span> : null}
							</span>
							<span className="sub">
								<span className="tel">{p.phone_e164 ? telefonoConLada(p.phone_e164) : "sin teléfono"}</span>
								<span className={"chip " + etapaClase(p.etapa_id)}>{det.etapa}</span>
								{esSpam ? <span className="chip">Spam</span> : null}
								{det.wapp?.ofertas?.length ? <span className="chip objetivo solo-pc">Objetivo: {Array.from(new Set(det.wapp.ofertas.map((o) => ({ promo: "Promo", kit: "Kit", mas_cursos: "Más cursos", retencion: "Retención", reingreso: "Reinscripción" } as any)[o.kind] || o.kind))).join(", ")}</span> : null}
								{/* El correlativo del alumno vive en la Ficha: el encabezado queda como la maqueta (Esteban, 2026-10-15). */}
								{/* Teléfono: la próxima actividad bajo el nombre (Esteban, 2026-10-05: "hay que hacer scroll a los lados"). */}
								<span className="solo-movil cv-sub-viene" onClick={(e) => e.stopPropagation()}>
									{av.soloVer ? null : <LoQueViene det={det} campusId={campusId} corto onAgendar={() => { setSegMotivo(p.next_action_reason || ""); setPanel("agendar"); }} />}
								</span>
							</span>
							</span>
							{/* El origen ("WhatsApp, anuncio, desde…") ya está en la Ficha ("Llegó por", "Primer contacto"); la dueña de Ventas, aquí. */}
							<EquipoLinea equipo={det.equipo} venta={p.owner_zoho_name} ventaId={p.owner_zoho_id} onPerfil={verPerfil} ventaMia={!!p.owner_zoho_id && p.owner_zoho_id === catalogs?.myZohoId} />
						</span>
					</div>
					<div className="cli-acc">
						<button className="btn-wa cv-b llamar cv-ico" disabled={!p.phone_e164 || (tel.ocupado && tel.llamada?.prospectId !== prospectId)} aria-label="Llamar" title="Llamar"
							onClick={() => {
								// Ya en llamada con esta persona: el botón trae el panel de la llamada (para colgar), no marca otra vez.
								if (tel.ocupado && tel.llamada?.prospectId === prospectId) { mostrarLlamada(); return; }
								setFin(null); llamarAhora().then((ok) => { if (!ok && !telefono.ocupado) setModal("llamar"); });
							}}>
							<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z" /></svg>
							<span className="solo-pc">{tel.ocupado && tel.llamada?.prospectId === prospectId ? "En llamada" : "Llamar"}</span>
						</button>
						{av.soloVer ? null : <button className="btn-wa cv-b agendar solo-pc" onClick={() => { setSegMotivo(""); setPanel(panel === "agendar" ? null : "agendar"); }}>Agendar</button>}
						{/* Without a row in this area yet (Kits found by search, 2026-10-15) there is nothing to take or resolve: it appears once they write. */}
						{bandeja && enEsta ? (
							<span className="solo-pc cv-bandeja-acc">
								{enEsta?.assigned_user_id && enEsta.assigned_user_id === yoId
									? <button className="btn-wa" disabled={busy} onClick={() => cambiarBandeja("soltar")}>Soltar</button>
									: <button className="btn-wa cv-b tomar" disabled={busy} onClick={() => cambiarBandeja("tomar")}>Tomar</button>}
								{enEsta?.estado === "resuelta"
									? <button className="btn-wa" disabled={busy} onClick={() => cambiarBandeja("reabrir")}>Reabrir</button>
									: <button className="btn-wa cv-b resuelto" disabled={busy} onClick={() => cambiarBandeja("resolver")}>Resuelto</button>}
							</span>
						) : null}
						{/* Reasignar a la vista y "Pasar a otro departamento" al menú ⋮ (Esteban, 2026-10-10: reasignar es mucho más común). */}
						{(catalogs?.puedeReasignar ?? catalogs?.isSupervisor) ? <button className="btn-wa solo-pc" onClick={() => setPanel(panel === "reasignar" ? null : "reasignar")}>Reasignar</button> : null}
						{!bandeja && !av.soloVer && p.etapa_id === 1 ? <button className="btn-wa cv-b inscribir solo-pc" onClick={() => navigate("/enrollmentPrerequests", { state: { nuevaSolicitud: { phone: p.phone_e164, name: nombreReal(p) || "", plantel: p.campus_id || null, curso: String(p.course_interest_text || "").split(/[,;\n]/)[0].trim() } } })}>Inscribir</button> : null}
						{!bandeja && !av.soloVer && (p.etapa_id === 3 || p.etapa_id === 4) ? <button className="btn-wa cv-b inscribir solo-pc" onClick={() => navigate("/enrollmentRequests", { state: { phone: p.phone_e164, name: nombreReal(p) || "", reingreso: true } })}>Reinscribir</button> : null}
						<button className={"btn-wa cv-ficha-btn solo-pc" + (verFicha ? " on" : "")} aria-pressed={verFicha} onClick={alternarFicha}>Ficha</button>
						<button className={"btn-wa cv-ico cv-buscar-btn" + (buscarAbierto ? " on" : "")} aria-pressed={buscarAbierto} title="Buscar en la conversación" aria-label="Buscar en la conversación"
							onClick={() => setBuscarAbierto(!buscarAbierto)}>
							<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
						</button>
						<div className="cv-menu">
							<button className="btn-wa cv-ico cv-puntos" aria-haspopup="menu" aria-expanded={mas} onClick={() => setMas(!mas)} aria-label="Más opciones">⋮</button>
							{mas ? <div className="wl-velo" onClick={() => setMas(false)} aria-hidden="true" /> : null}
							{mas ? (
								<div className="cv-menu-lista" role="menu" onClick={() => setMas(false)}>
									{av.soloVer ? null : <button role="menuitem" onClick={() => setPanel("pasar")}>Pasar a otro departamento</button>}
									{catalogs?.contratoWa && det.student && !av.soloVer ? <button role="menuitem" onClick={() => { setPanel("contrato"); setContratos(null); setContratoSel(null);
										http.prospectsService.contratosWa(campusId, prospectId).then((r) => { setContratos(r.data.contratos); setContratoSel(r.data.contratos[0]?.id ?? null); }).catch((e) => { setContratos([]); setError(errorTexto(e)); }); }}>Enviar Contrato</button> : null}
									{!bandeja && !av.soloVer && p.etapa_id === 1 ? <button role="menuitem" className="solo-movil" onClick={() => navigate("/enrollmentPrerequests", { state: { nuevaSolicitud: { phone: p.phone_e164, name: nombreReal(p) || "", plantel: p.campus_id || null, curso: String(p.course_interest_text || "").split(/[,;\n]/)[0].trim() } } })}>Inscribir</button> : null}
									{!bandeja && !av.soloVer && (p.etapa_id === 3 || p.etapa_id === 4) ? <button role="menuitem" className="solo-movil" onClick={() => navigate("/enrollmentRequests", { state: { phone: p.phone_e164, name: nombreReal(p) || "", reingreso: true } })}>Reinscribir</button> : null}
									<button role="menuitem" className="solo-movil" onClick={() => setVistaMovil("cliente")}>Ficha del cliente</button>
									{!bandeja && !av.soloVer && det.wapp?.active && det.wapp.promo?.best ? <button role="menuitem" onClick={() => setModal("promo")}>Promo que aplica</button> : null}
									{det.student ? <button role="menuitem" onClick={() => navigate(`/students?alumno=${det.student!.id}&plantel=${det.wapp?.active?.campus_id || campusId}`)}>Ficha de alumna</button> : null}
									{onVerCrm ? <button role="menuitem" className="solo-pc" onClick={() => onVerCrm(prospectId)}>Ver en CRM</button> : null}
									<button role="menuitem" onClick={async () => { try { await http.prospectsService.noLeido(campusId, prospectId); onChanged(); onBack(); } catch (e) { setError(errorTexto(e)); } }}>Marcar como no leído</button>
									{!bandeja && !av.soloVer && p.phone_e164 ? <button role="menuitem" onClick={alMarcador} title="Lo pone hasta arriba de tu lista de hoy">Al Marcador ↑</button> : null}
									{av.soloVer ? null : <button role="menuitem" onClick={() => setModal("editar")}>Editar datos</button>}
									{av.soloVer ? null : <button role="menuitem" onClick={() => setPanel("vincular")}>Vincular con otra persona</button>}
									<button role="menuitem" onClick={() => { setPanel("compartir"); http.prospectsService.equipoCanales(campusId).then((r) => setCanalesEq(r.data.canales)).catch(() => setCanalesEq([])); }}>Compartir en WappTeam</button>
									{(catalogs?.puedeReasignar ?? catalogs?.isSupervisor) ? <button role="menuitem" className="solo-movil" onClick={() => setPanel("reasignar")}>Reasignar</button> : null}
									{!esSpam && !av.soloVer && catalogs?.statuses.some((x) => x.id === 8) ? <button role="menuitem" className="peligro" onClick={marcarSpam}>Marcar como spam</button> : null}
								</div>
							) : null}
						</div>
					</div>
				</div>

				{/* Status como lista (Esteban, 2026-10-04: "make list"), lo que viene, y qué enseñar de la conversación: todo en una línea. */}
				<div className="cab-fila dos">
					{bandeja ? (
						<span className="cv-bandeja-info">
							<span className="chip etapa1">{DEPTS_WAPP[bandeja.dept] || bandeja.dept}</span>
							{enEsta?.estado === "resuelta" ? <span className="chip">Resuelto</span> : null}
							<span>{enEsta?.assigned_name ? "Lo atiende " + enEsta.assigned_name.split(" ")[0] : "Sin asignar"}</span>
							{enEsta ? <span className="solo-movil cv-bandeja-acc">
								{enEsta?.assigned_user_id && enEsta.assigned_user_id === yoId
									? <button className="btn-wa" disabled={busy} onClick={() => cambiarBandeja("soltar")}>Soltar</button>
									: <button className="btn-wa cv-b tomar" disabled={busy} onClick={() => cambiarBandeja("tomar")}>Tomar</button>}
								{enEsta?.estado === "resuelta"
									? <button className="btn-wa" disabled={busy} onClick={() => cambiarBandeja("reabrir")}>Reabrir</button>
									: <button className="btn-wa cv-b resuelto" disabled={busy} onClick={() => cambiarBandeja("resolver")}>Resuelto</button>}
							</span> : null}
							{enEsta?.pasado_por_name ? <span className="apagado">Te lo pasó {enEsta.pasado_por_name.split(" ")[0]}{enEsta.pasado_nota ? ": " + enEsta.pasado_nota : ""}</span> : null}
							<span className="apagado solo-pc">{det.etapa}{p.owner_zoho_name ? ", asesora " + p.owner_zoho_name.split(" ")[0] : ""}</span>
						</span>
					) : av.soloVer ? (
						<span className="cv-bandeja-info"><span className="chip">{det.status}</span><span className="apagado">Es de {av.duena}</span></span>
					) : catalogs ? (
						<span className="cv-status-sel">
							{/* Potencial con sus motivos en el mismo selector: se elige y se guarda de un clic (Esteban, 2026-10-10). */}
							<select value={String(p.status_id) + (p.potencial_motivo && catalogs.statuses.find((s) => s.id === p.status_id)?.bool_needs_reason ? "|" + p.potencial_motivo : "")} disabled={busy} aria-label="Status"
								onChange={(e) => { const [id, m] = e.target.value.split("|"); ponerStatus(Number(id), m || null); }}>
								{catalogs.statuses.filter((s) => s.bool_manual || s.id === p.status_id).map((s) => s.bool_needs_reason ? (
									<optgroup key={s.id} label={s.name}>
										{p.status_id === s.id && p.potencial_motivo && !catalogs.motivosPotencial.filter((m) => !["heredado de Zoho", "sin status en Zoho"].includes(m)).includes(p.potencial_motivo)
											? <option value={s.id + "|" + p.potencial_motivo} disabled>{s.name} · {p.potencial_motivo}</option> : null}
										{catalogs.motivosPotencial.filter((m) => !["heredado de Zoho", "sin status en Zoho"].includes(m)).map((m) => <option key={m} value={s.id + "|" + m}>{s.name} · {m}</option>)}
									</optgroup>
								) : (
									<option key={s.id} value={s.id} disabled={!s.bool_manual && s.id !== p.status_id}>{s.name}</option>
								))}
							</select>
							{pideMotivo ? (
								<span className="cv-st-motivo">
									<select value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo" autoFocus>
										<option value="">Motivo…</option>
										{catalogs.motivosPotencial.filter((m) => !["heredado de Zoho", "sin status en Zoho"].includes(m)).map((m) => <option key={m} value={m}>{m}</option>)}
									</select>
									<button className="btn-wa pri" disabled={busy || !motivo} onClick={guardarStatus}>Guardar</button>
									<button className="btn-wa" onClick={() => setPideMotivo(null)}>Cancelar</button>
								</span>
							) : null}
						</span>
					) : null}
					{!bandeja && enBandejas.filter((b) => b.estado === "abierta").map((b) => (
						<span key={b.dept} className="chip solo-pc" title={b.assigned_name ? "Lo atiende " + b.assigned_name : "Sin asignar"}>En {DEPTS_WAPP[b.dept] || b.dept}</span>
					))}
					{/* Teléfono: Nota, Agendar y CRM a la vista (Esteban, 2026-10-05). */}
					<span className="solo-movil cv-acc-movil">
						{av.puedeNota ? <button className="btn-wa cv-pill nota" onClick={() => escribir("nota")}>+ Nota</button> : null}
						{av.soloVer ? null : <button className="btn-wa cv-pill agendar" onClick={() => { setSegMotivo(""); setPanel(panel === "agendar" ? null : "agendar"); }}>Agendar</button>}
						{onVerCrm ? <button className="btn-wa cv-pill" onClick={() => onVerCrm(prospectId)}>CRM</button> : null}
					</span>
					{av.soloVer ? null : <span className="solo-pc"><LoQueViene det={det} campusId={campusId} corto onAgendar={() => { setSegMotivo(p.next_action_reason || ""); setPanel("agendar"); }} /></span>}
					<span className="cv-ver">
						<select value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Qué enseñar">
							{KIND_FILTROS.map((f) => {
								// "Todo" suma todos los tipos, no lo que está cargado con el filtro de ahora (barrido 26-oct, C6).
								const n = f.kinds.length ? det.eventCounts.filter((c) => f.kinds.includes(c.kind)).reduce((t, c) => t + Number(c.n), 0)
									: det.eventCounts.reduce((t, c) => t + Number(c.n), 0) || total;
								return <option key={f.key} value={f.key}>{"Ver: " + f.label + (n ? " (" + n.toLocaleString("es-MX") + ")" : "")}</option>;
							})}
						</select>
					</span>
				</div>
			</div>

			{buscarAbierto ? <BuscadorChat llave={prospectId + "|" + kinds.join(",")} pedir={buscarEnChat} onIr={(id) => { setVistaMovil("chat"); saltar(id); }} onCerrar={() => setBuscarAbierto(false)} /> : null}
			<AvisoSoloVer acceso={av} className="cv-solo-ver" />
			{error ? <div className="aviso error" style={{ margin: "10px 18px 0" }}>{error}</div> : null}

			{panel === "agendar" ? (
				<div className="tarjeta cv-panel">
					<h4>{segMotivo === "Promesa de visita" ? "Promesa de visita" : "Agendar seguimiento"}</h4>
					<input type="date" value={segFecha} onChange={(e) => setSegFecha(e.target.value)} aria-label="Día" />
					{segHora === null
						? <button type="button" className="fc-mas-hora" onClick={() => setSegHora("")}>+ Agregar hora <small>(opcional)</small></button>
						: <span className="cv-seg-hora"><input type="time" value={segHora} onChange={(e) => setSegHora(e.target.value)} aria-label="Hora" />
							<button type="button" className="fc-mas-hora" onClick={() => setSegHora(null)}>Sin hora</button></span>}
					<input type="text" placeholder="Para qué (visita, 2ª llamada, manda comprobante…)" value={segMotivo} onChange={(e) => setSegMotivo(e.target.value)} aria-label="Para qué" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !segFecha} onClick={guardarAgenda}>Guardar</button>
						{p.next_action_at ? <button className="btn-wa" disabled={busy} onClick={() => guardarSeguimiento(true)}>Quitar el que tiene</button> : null}
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "compartir" ? (
				<div className="tarjeta cv-panel">
					<h4>Compartir en WappTeam</h4>
					<select value={compartirEn} onChange={(e) => setCompartirEn(e.target.value)} aria-label="Canal o compañera">
						<option value="">{canalesEq.length ? "Elegir canal o compañera…" : "Cargando…"}</option>
						{canalesEq.map((c) => <option key={c.id} value={c.id}>{(c.tipo === "canal" ? "# " : "") + c.nombre}</option>)}
					</select>
					<input type="text" placeholder="Mensaje (opcional)" value={compartirNota} onChange={(e) => setCompartirNota(e.target.value)} aria-label="Mensaje para el equipo" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !compartirEn} onClick={() => accion(async () => {
							await http.prospectsService.equipoEnviar(campusId, Number(compartirEn), compartirNota.trim() || null, prospectId);
							setCompartirEn(""); setCompartirNota("");
						})}>Compartir</button>
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "contrato" ? (
				<div className="tarjeta cv-panel">
					<h4>Enviar Contrato</h4>
					{contratos === null ? <small className="rd-nota">Cargando contratos…</small> : contratos.length === 0 ? <small className="rd-nota">Esta persona no tiene contratos listos en WAPP.</small> : (
						<select value={contratoSel || ""} onChange={(e) => setContratoSel(e.target.value ? Number(e.target.value) : null)} aria-label="Contrato">
							{contratos.map((c) => <option key={c.id} value={c.id}>{(c.curso || c.nombre || "Contrato") + (c.horario ? " · " + c.horario : "") + " · " + c.fecha + (c.firmado ? " · firmado" : "")}</option>)}
						</select>
					)}
					<small className="rd-nota">{(contratos || []).find((c) => c.id === contratoSel)?.firmado
						? "Sale el contrato firmado."
						: "Sale el enlace para que lo lea y lo firme desde su celular; al firmar le llega su copia."}</small>
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !contratoSel} onClick={() => contratoSel && accion(() => http.prospectsService.sendWhatsapp(campusId, prospectId, { contratoId: contratoSel, channelId: canal }))}>Enviar</button>
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "pasar" ? (
				<div className="tarjeta cv-panel">
					<h4>Pasar a otro departamento</h4>
					<select value={pasarA} onChange={(e) => setPasarA(e.target.value)} aria-label="Departamento">
						<option value="">Elegir departamento…</option>
						{Object.keys(DEPTS_WAPP).filter((d) => d !== bandeja?.dept && d !== "rrhh").map((d) => <option key={d} value={d}>{DEPTS_WAPP[d]}</option>)}
					</select>
					{catalogs?.isSupervisor && pasarA && ["caja", "kits", "academico"].includes(pasarA) ? (pasarPlanteles ? (
						<select value={pasarPlantel || ""} onChange={(e) => setPasarPlantel(e.target.value ? Number(e.target.value) : null)} aria-label="Plantel de destino">
							<option value="">Su plantel</option>
							{pasarPlanteles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
						</select>
					) : (
						<button type="button" className="enlace rd-otro" onClick={() => http.prospectsService.otroPlantel(campusId)
							.then((r) => setPasarPlanteles(r.data.planteles || [])).catch((e) => setError(errorTexto(e)))}>Otro plantel…</button>
					)) : null}
					<input type="text" placeholder="Qué necesita (queda en la conversación)" value={pasarNota} onChange={(e) => setPasarNota(e.target.value)} aria-label="Nota para el otro departamento" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !pasarA} onClick={guardarPasar}>Pasar</button>
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "reasignar" ? (
				<div className="tarjeta cv-panel">
					<h4>Reasignar</h4>
					<ReasignarDestino campusId={campusId} asesoras={catalogs?.advisors || []} value={{ advisorId: reasignarA, plantel: reasignarPlantel }}
						onChange={(v) => { setReasignarA(v.advisorId); setReasignarPlantel(v.plantel); }}
						etiqueta={(a) => a.name + (a.roles && a.roles[0] ? " — " + a.roles[0] : "")} />
					<input type="text" placeholder="Motivo (queda en la bitácora)" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={busy || !reasignarA} onClick={guardarReasignar}>Reasignar</button>
						<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button>
					</div>
				</div>
			) : null}

			{panel === "vincular" && catalogs ? (
				<div className="tarjeta cv-panel">
					<h4>Vincular con otra persona</h4>
					<div className="fila-botones">
						<input type="tel" placeholder="Teléfono de la otra persona" value={vincTel} onChange={(e) => setVincTel(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter" && vincTel.trim()) buscarVinculo(); }} aria-label="Teléfono de la otra persona" />
						<button className="btn-wa" onClick={buscarVinculo} disabled={!vincTel.trim()}>Buscar</button>
					</div>
					{vincMatch?.id ? (
						<>
							<div className="par"><span>Encontrado</span><span>{nombreCompleto(vincMatch)} · {vincMatch.etapa}</span></div>
							<select value={vincTipo} onChange={(e) => setVincTipo(e.target.value)} aria-label="Tipo de vínculo">
								{catalogs.linkKinds.map((k) => <option key={k} value={k}>{k === "padre_madre" ? "padre o madre" : k.replace(/_/g, " ")}</option>)}
							</select>
							<div className="fila-botones"><button className="btn-wa pri" disabled={busy} onClick={guardarVinculo}>Vincular</button></div>
						</>
					) : vincMatch?.none ? <div className="aviso">{vincMatch.valid ? "Ese teléfono no está registrado." : "Ese teléfono no es válido."}</div> : null}
				</div>
			) : null}

			<div className={"cv-cuerpo" + (vistaMovil === "cliente" ? " ver-cliente" : "") + (verFicha ? "" : " sin-ficha")}>
				<div className="cv-centro">
					<Conversacion key={filtro} onPerfil={verPerfil} campusId={campusId} prospectId={prospectId} events={eventosVista} hayMas={events.length < total} salto={salto} nombreCliente={(p.first_name || "").split(" ")[0] || undefined}
						onMas={() => cargarEventos(page + 1, "anteriores")} cargando={cargando}
						onResponder={(ev) => { setRespondiendo({ id: ev.id, body: ev.body, nuestro: ev.direction === "out" }); escribir("mensaje"); }}
						onReaccionar={async (ev, emoji) => {
							setError(null);
							try { await http.prospectsService.whatsappReaccion(campusId, prospectId, ev.id, emoji); cargarEventos(1, "reset"); }
							catch (e) { setError(errorTexto(e)); }
						}} />
					{av.puedeNota ? <Compositor campusId={campusId} prospectId={prospectId} firstName={(p.first_name || "").split(" ")[0]} countryId={p.country_id} soloNota={av.soloVer}
						wa={wa} texto={texto} setTexto={setTexto} ubicacion={ubicacion}
						onEnviado={() => { cargarEventos(1, "nuevos"); cargarWa(); cargarDetalle(); onChanged(); }} onSale={onSale} onSalio={onSalio}
						onCanal={(id) => { setCanal(id); cargarWa(id); }} modo={modoComp} setModo={setModoComp} foco={foco}
						respondiendo={respondiendo} onCancelarRespuesta={() => setRespondiendo(null)}
						plantelId={p.campus_id || campusId} cursoInteres={p.course_interest_text} nombreCliente={(p.first_name || "").split(" ")[0] || undefined} /> : null}
				</div>
				<button type="button" className="cv-ficha-velo" aria-label="Cerrar la ficha" tabIndex={-1} onClick={alternarFicha} />
				<button type="button" className="cv-ficha-x" aria-label="Cerrar la ficha" onClick={alternarFicha}>×</button>
				<LadoCliente campusId={campusId} det={det} onEditar={() => setModal("editar")}
					onEnviarAlChat={(t) => setTexto((texto ? texto + "\n\n" : "") + t)} onPaso={paso} onAbrir={onOpen} onQuitarVinculo={quitarVinculo}
					onPromo={() => setModal("promo")} onAgendar={(motivo) => { setSegMotivo(motivo); setPanel("agendar"); }}
					catalogs={catalogs} onAccion={accion} />
			</div>

			{modal === "editar" ? <EditarDatos campusId={campusId} det={det} catalogs={catalogs} onClose={() => setModal(null)}
				onGuardado={(d) => { setDet(d); setModal(null); cargarEventos(1, "nuevos"); onChanged(); }} /> : null}
			{modal === "promo" ? <PromoModal campusId={campusId} det={det} asesora={p.owner_zoho_name || ""} onClose={() => setModal(null)}
				onAlChat={(t) => setTexto(t)} wa={wa} onEnviado={() => { cargarEventos(1, "nuevos"); cargarWa(); cargarDetalle(); onChanged(); }}
				onVerLista={onVista ? () => onVista(2, "califican_promo") : undefined} /> : null}
			{avisoLlamada ? <div role="status" style={{ position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)", zIndex: 2100, background: "#2B1C16", color: "#fff", padding: "10px 18px", borderRadius: 14, fontSize: 14, boxShadow: "0 10px 30px rgba(43,28,22,.3)" }}>{avisoLlamada}</div> : null}
			{cita ? <ModalCita campusId={campusId} prospectId={prospectId} plantelId={p.campus_id || campusId} modo="cita" promesa={cita.promesa}
				aviso={cita.promesa ? "Una Promesa lleva fecha: agenda la cita o el seguimiento." : null}
				onGuardado={() => { setCita(null); cargarDetalle(); cargarEventos(1, "reset"); onChanged(); autoMarcador.refrescar(); }} onCancelar={() => setCita(null)} /> : null}
			{modal === "llamar" && p.phone_e164 ? <Llamada campusId={campusId} prospectId={prospectId} nombre={nombreCompleto(p)} telefono={p.phone_e164}
				origen={marcador ? "marcador" : "manual"} auto={fin} campaignContactId={marcador?.campaignContactId || null} onClose={() => { setModal(null); setFin(null); }}
				onGuardado={(r) => { setModal(null); cargarEventos(1, "nuevos"); cargarDetalle(); onChanged(); marcador?.onResultado(r); }} /> : null}
			{tarjeta ? <PerfilPersona campusId={campusId} ancla={tarjeta} onCerrar={() => setTarjeta(null)}
				onMensaje={async (id) => { setTarjeta(null); try { const r = await http.prospectsService.equipoDirecto(campusId, id); navigate("/wappteam?c=" + r.data.id); } catch { /* sin directo: se queda aquí */ } }}
				onAbrirCanal={(id) => { setTarjeta(null); navigate("/wappteam?c=" + id); }} /> : null}
		</div>
	);
};

export default ClienteView;
